<template>
  <div class="v1-header">
    <!-- Barra de modo: o dropdown ocupa a barra inteira; menu full-width -->
    <b-dropdown
      block
      no-caret
      variant="light"
      class="v1-mode-dd"
      toggle-class="v1-mode-toggle"
      menu-class="w-100 v1-mode-menu"
    >
      <template #button-content>
        <span class="v1-mode-left">
          <span class="v1-mode-prefix">Operando como</span>
          <i class="bx bx-chevron-down"></i>
        </span>
        <span class="v1-mode-tag" :class="'tag-' + stationMode">
          <i class="bx" :class="current.icon"></i>
          {{ current.label }}
        </span>
      </template>

      <b-dropdown-item
        v-for="m in allowedModes"
        :key="m.key"
        :active="stationMode === m.key"
        @click="selectMode(m.key)"
      >
        <i class="bx opt-icon" :class="m.icon"></i>
        <span class="opt-label">{{ m.label }}</span>
        <small class="opt-desc">{{ m.desc }}</small>
      </b-dropdown-item>
    </b-dropdown>

    <!-- Abas de filtro por status -->
    <div class="v1-tabs-row">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="v1-tab"
        :class="{ active: value === t.key }"
        @click="changeButtonHandle(t.key)"
      >
        {{ t.label }}
        <span v-if="tabCounts[t.key] != null" class="tab-count">{{ tabCounts[t.key] }}</span>
      </button>
    </div>

    <!-- Busca (+ botão que revela o filtro por brinde, só no modo Brinde) -->
    <div class="v1-search-row">
      <div class="v1-search">
        <input type="text" v-model="search" placeholder="Buscar por nome ou e-mail...">
      </div>
      <button
        v-if="stationMode === 'brinde' && giftCatalog.length"
        type="button"
        class="v1-filter-btn"
        :class="{ active: showBrindeFilter }"
        :title="showBrindeFilter ? 'Ocultar filtro por brinde' : 'Filtrar por brinde'"
        @click="showBrindeFilter = !showBrindeFilter"
      >
        <i class="bx bx-slider-alt"></i>
        <span v-if="brindeFilter !== null" class="v1-filter-dot"></span>
      </button>
    </div>

    <!-- Filtro por brinde (revelado pelo botão): escopa a lista a um brinde -->
    <div
      v-if="stationMode === 'brinde' && showBrindeFilter && giftCatalog.length"
      class="v1-brinde-filter"
    >
      <div class="v1-bf-label">Filtrar por brinde</div>
      <div class="v1-bf-chips">
        <button
          type="button"
          class="v1-bf-chip"
          :class="{ active: brindeFilter === null }"
          @click="selectBrindeFilter(null)"
        >Todos</button>
        <button
          v-for="g in giftCatalog"
          :key="g.id"
          type="button"
          class="v1-bf-chip"
          :class="{ active: brindeFilter === g.id }"
          @click="selectBrindeFilter(g.id)"
        >
          <i class="bx bx-gift"></i>
          {{ g.name }}
          <span v-if="g.eligibleCount != null" class="v1-bf-count">{{ g.eligibleCount }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Swal from 'sweetalert2'
import { CHECK_IN_MODULE } from '@/modules/check-in/store'

const MODES = [
  { key: 'checkin', label: 'Check-in', icon: 'bx-user-check', desc: 'Registra a presença no evento' },
  { key: 'lead', label: 'Coleta de Lead', icon: 'bx-clipboard', desc: 'Lê e abre o formulário de coleta' },
  { key: 'brinde', label: 'Brinde', icon: 'bx-gift', desc: 'Marca a retirada do brinde' }
]

export default {
  name: 'CheckInHeader',
  props: {
    onFilter: {
      type: Function,
      default: () => null
    }
  },
  data: () => ({
    modes: MODES,
    value: 'TODOS',
    search: '',
    showBrindeFilter: false
  }),
  computed: {
    ...mapGetters(CHECK_IN_MODULE, ['stationMode', 'checkinCounts', 'giftCatalog', 'brindeFilter']),
    // Estações liberadas pro operador logado (allowed_stations). null/[] = todas.
    // Super admin também não tem restrição.
    allowedModes () {
      const u = this.$store.state.auth && this.$store.state.auth.user
      if (u && u.is_super_admin) return MODES
      const allowed = u && u.allowed_stations
      if (!Array.isArray(allowed) || allowed.length === 0) return MODES
      const filtered = MODES.filter(m => allowed.includes(m.key))
      return filtered.length ? filtered : MODES
    },
    current () {
      return MODES.find(m => m.key === this.stationMode) || MODES[0]
    },
    // Mapeia os contadores do store para as chaves das abas (a 3ª = concluídos).
    tabCounts () {
      const c = this.checkinCounts || {}
      return { TODOS: c.total, AGUARDANDO: c.aguardando, IN: c.done }
    },
    tabs () {
      // A 3ª aba muda de rótulo por modo (mesma chave 'IN' = "os já concluídos").
      const third = { checkin: 'In', lead: 'Coletados', brinde: 'Entregue' }[this.stationMode] || 'In'
      return [
        { key: 'TODOS', label: 'Todos' },
        { key: 'AGUARDANDO', label: 'Aguardando' },
        { key: 'IN', label: third }
      ]
    }
  },
  mounted () {
    this.enforceAllowedMode()
    if (this.stationMode === 'brinde') this.loadGiftCatalog()
  },
  methods: {
    ...mapMutations(CHECK_IN_MODULE, ['setStationMode', 'setBrindeFilter']),
    ...mapActions(CHECK_IN_MODULE, ['loadGiftCatalog']),
    // Se o modo salvo (por dispositivo) não está entre os liberados pro operador,
    // cai no primeiro liberado. Sem Swal — é ajuste automático de permissão.
    enforceAllowedMode () {
      const keys = this.allowedModes.map(m => m.key)
      if (keys.length && !keys.includes(this.stationMode)) {
        this.setStationMode(keys[0])
      }
    },
    selectBrindeFilter (id) {
      this.setBrindeFilter(id)
    },
    async selectMode (mode) {
      if (mode === this.stationMode) return
      const labels = { checkin: 'Check-in', lead: 'Coleta de Lead', brinde: 'Brinde' }
      const { value } = await Swal.fire({
        title: 'Trocar modo de operação?',
        html: `Esta estação passará a operar como <b>${labels[mode]}</b>.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#556ee6',
        cancelButtonColor: '#74788d',
        confirmButtonText: 'Sim, trocar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      })
      if (!value) return
      this.setStationMode(mode)
    },
    changeButtonHandle (value) {
      this.value = value
    },
    dispatchChange () {
      const filter = {}
      if (this.value === 'AGUARDANDO') filter.onlyWaiting = true
      if (this.value === 'IN') filter.onlyCheckIn = true
      if (this.search) filter.search = this.search
      this.onFilter(filter)
    }
  },
  beforeDestroy () {
    clearTimeout(this._searchTimer)
  },
  watch: {
    value: 'dispatchChange',
    // O user do auth pode carregar async (loadSession no refresh): quando as
    // estações liberadas mudarem, reforça o modo permitido.
    allowedModes: 'enforceAllowedMode',
    // Busca com debounce: dispara ~300ms depois de parar de digitar (evita uma
    // requisição por tecla e o resultado "fora de ordem").
    search () {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => this.dispatchChange(), 300)
    },
    stationMode (mode) {
      // Ao entrar no modo brinde, carrega o catálogo pros chips; ao sair, limpa
      // o filtro pra não vazar pros outros modos.
      if (mode === 'brinde') this.loadGiftCatalog()
      else if (this.brindeFilter !== null) this.setBrindeFilter(null)
    }
  }
}
</script>

<style scoped lang="scss">
.v1-header {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  /* Sem overflow:hidden — ele cortava o menu do dropdown de modo ("comendo as
     pontas"). O topo da barra de modo é arredondado abaixo pra manter o visual. */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 18px;
}

/* ── Barra de modo: o toggle É a barra inteira ── */
.v1-mode-dd {
  display: block;
  width: 100%;
}

::v-deep .v1-mode-toggle {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  background: #f8f9fb;
  border: none;
  border-bottom: 1px solid #e4e7ed;
  border-radius: 8px 8px 0 0; /* casa com o topo arredondado do header */
  box-shadow: none;
  color: #1a1d26;

  &:hover, &:focus, &:active {
    background: #f2f4f8;
    color: #1a1d26;
    box-shadow: none;
  }
}

.v1-mode-left {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1d26;

  .bx-chevron-down { color: #9ca3b4; font-size: 16px; }
}

.v1-mode-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  border: 1px solid transparent;

  i { font-size: 14px; }

  &.tag-checkin { background: #e8f5e9; border-color: #c8e6c9; color: #2e7d32; }
  &.tag-lead    { background: #e3f2fd; border-color: #bbdefb; color: #1565c0; }
  &.tag-brinde  { background: #fff3e0; border-color: #ffe0b2; color: #e8860c; }
}

/* Menu full-width, itens com ícone + descrição (design de antes) */
::v-deep .v1-mode-menu {
  margin-top: 4px;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  padding: 8px;
  min-width: auto;

  .dropdown-item {
    display: grid;
    grid-template-columns: 34px 1fr;
    grid-template-rows: auto auto;
    align-items: center;
    column-gap: 10px;
    padding: 11px 14px;
    border-radius: 8px;
    white-space: normal;

    .opt-icon {
      grid-row: 1 / 3;
      width: 34px;
      height: 34px;
      display: grid;
      place-items: center;
      border-radius: 8px;
      background: #f2f4f8;
      color: #6b7280;
      font-size: 18px;
    }

    .opt-label { font-weight: 700; color: #1a1d26; }
    .opt-desc { grid-column: 2; color: #8b90a5; font-size: 12px; }

    &:hover { background: #f6f7fb; }

    &.active, &:active {
      background: #eef1fe;
      .opt-icon { background: #556ee6; color: #fff; }
      .opt-label { color: #556ee6; }
    }
  }
}

/* ── Abas ── */
.v1-tabs-row {
  display: flex;
  padding: 12px 16px 0;
  gap: 4px;
}

.v1-tab {
  flex: 1 1 0;
  min-width: 0; /* deixa dividir a largura igualmente sem estourar/cortar */
  padding: 10px 8px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9ca3b4;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;

  &.active {
    color: #556ee6;
    border-bottom-color: #556ee6;
  }
}

.tab-count {
  font-size: 0.625rem;
  background: #eef0f4;
  padding: 2px 7px;
  border-radius: 6px;
  font-weight: 700;
  color: #9ca3b4;
}

.v1-tab.active .tab-count {
  background: #eef1fe;
  color: #556ee6;
}

.tab-count {
  flex-shrink: 0;
}

/* Mobile: reduz para os 3 tabs (Todos/Aguardando/Entregue|Coletados) caberem */
@media (max-width: 575px) {
  .v1-tabs-row {
    padding: 12px 8px 0;
    gap: 2px;
  }
  .v1-tab {
    padding: 9px 4px;
    font-size: 0.6875rem;
    gap: 4px;
  }
  .tab-count {
    font-size: 0.5625rem;
    padding: 1px 5px;
  }
}

/* ── Linha da busca + botão de filtro ── */
.v1-search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 14px;
}
.v1-search-row .v1-search {
  flex: 1 1 auto;
  padding: 0;
}
.v1-filter-btn {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 1.5px solid #e4e7ed;
  border-radius: 8px;
  background: #f8f9fb;
  color: #9ca3b4;
  cursor: pointer;
  transition: all 0.15s;

  i { font-size: 20px; }

  &:hover { border-color: #e8860c; color: #e8860c; background: #fffaf4; }
  &.active { background: #e8860c; border-color: #e8860c; color: #fff; }
}
.v1-filter-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e8860c;
  box-shadow: 0 0 0 2px #fff;
}
.v1-filter-btn.active .v1-filter-dot { background: #fff; box-shadow: 0 0 0 2px #e8860c; }

/* ── Filtro por brinde (chips) ── */
.v1-brinde-filter {
  padding: 0 16px 16px;
}
.v1-bf-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3b4;
  margin-bottom: 8px;
}
.v1-bf-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.v1-bf-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1.5px solid #e4e7ed;
  border-radius: 999px;
  background: #fff;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  color: #4a5068;
  cursor: pointer;
  transition: all 0.15s;

  i { font-size: 14px; color: #e8860c; }

  &:hover { border-color: #e8860c; background: #fffaf4; }

  &.active {
    background: #e8860c;
    border-color: #e8860c;
    color: #fff;
    i { color: #fff; }
    .v1-bf-count { background: rgba(255, 255, 255, 0.25); color: #fff; }
  }
}
.v1-bf-count {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  background: #f0f1f5;
  color: #9ca3b4;
}

/* ── Busca ── */
.v1-search {
  padding: 12px 16px 14px;
}

.v1-search input {
  width: 100%;
  padding: 11px 14px 11px 38px;
  border: 1.5px solid #e4e7ed;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  color: #1a1d26;
  background: #f8f9fb url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15' height='15' fill='none' viewBox='0 0 16 16'%3E%3Ccircle cx='7' cy='7' r='5' stroke='%239ca3b4' stroke-width='1.5'/%3E%3Cpath d='M11 11l3 3' stroke='%239ca3b4' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E") 13px center no-repeat;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder { color: #b8bcc8; }
  &:focus { border-color: #556ee6; background-color: #fff; }
}
</style>
